/** Pure resolution for the active team; the provider is `ActiveTeamProvider.tsx`. */
import type { TeamRef } from "@authe/fe-commons/teams";

/**
 * The tenant's primary-team alias — what stands in until a real team is known.
 *
 * Its id is the tenant id, which `teams-mgmt-api`'s `resolve_team_id` maps onto
 * the caller's own default team. Its NAME is deliberately generic: it took the
 * tenant's display name, so every surface that renders `team.name` announced
 * the tenant as a team. The two are different scopes — a tenant holds teams,
 * and a share scoped to "my team" is not a share with the tenant — so naming
 * one after the other misdescribes who can see what.
 */
export function primaryTeamAlias(tenant: {
  orgId: string | null;
  tenantId: string | null;
}): TeamRef {
  return {
    id: tenant.tenantId ?? "default",
    name: "Your default team",
    tenant: { orgId: tenant.orgId, tenantId: tenant.tenantId },
  };
}

/**
 * Which team is effective, given the caller's teams and their stored pick.
 *
 * Pure so it can be tested without a provider (these packages render hooks with
 * `renderToStaticMarkup`, which runs no effects). Three rules:
 *
 * - a stored id the caller still holds wins;
 * - a stored id they no longer hold — removed from the team, or left over from
 *   a tenant they switched away from — falls back to their FIRST team, never to
 *   the alias, so the picker never sits on a value none of its options match;
 * - no teams at all resolves to the primary-team alias, which the backend maps
 *   onto the caller's own default team.
 */
export function resolveActiveTeam(input: {
  teams: TeamRef[];
  selectedTeamId: string | null;
  tenant: { orgId: string | null; tenantId: string | null };
}): { team: TeamRef; selectedTeamId: string | null; isFallback: boolean } {
  const { teams, selectedTeamId, tenant } = input;
  const held = teams.find((t) => t.id === selectedTeamId);
  const effective = held ?? teams[0] ?? null;
  return {
    team: effective ?? primaryTeamAlias(tenant),
    selectedTeamId: effective?.id ?? null,
    isFallback: effective === null,
  };
}
